import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, Globe, Megaphone, PenTool, BarChart3, Star } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { SERVICE_CATEGORIES, getServicesByCategory } from '@/lib/services';

const ICONS = {
  web: <Globe size={26} className="text-bp-teal" />,
  marketing: <Megaphone size={26} className="text-bp-blue" />,
  creative: <PenTool size={26} className="text-bp-coral" />,
  growth: <BarChart3 size={26} className="text-bp-amber" />,
};
const HALO = {
  web: 'bg-bp-teal-50', marketing: 'bg-bp-blue-50', creative: 'bg-orange-50', growth: 'bg-bp-amber-50',
};

export default function ServicesPage() {
  return (
    <main data-testid="services-page" className="bg-white">
      <SEO
        title="Digital Marketing Services - SEO, PPC, Web Design, Social Media & More"
        description="Full-service digital marketing: SEO, PPC/Google Ads, web design, social media, email marketing, reputation management & more. 19 years experience. Get a free quote!"
        path="/services"
      />

      {/* HERO */}
      <section className="relative pt-14 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal as="div" className="kicker"><span className="dot"/>Full-Service Digital Agency</Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[44px] sm:text-[68px] lg:text-[88px]">
              Award-Winning <br/>
              <span className="grad-text">Solutions</span> Since 2007.
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[17px] text-bp-slate leading-relaxed max-w-xl">
              From website design to comprehensive digital marketing, we provide everything your business needs to grow online.
              19 years of experience. 1k+ websites built.
            </Reveal>
            <Reveal as="div" delay={200} className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="services-hero-cta"><Phone size={15}/> Request A Free Estimate</Link>
              <Link to="/contact" className="btn-secondary">Contact Us <ArrowRight size={15}/></Link>
            </Reveal>
          </div>
          <Reveal as="div" delay={160} className="lg:col-span-5">
            <img
              src="/images/blog/white-label-reporting-tool.webp"
              alt="Brand Partners - White-label analytics dashboard"
              className="w-full h-auto rounded-3xl shadow-xl"
              loading="eager"
            />
          </Reveal>
        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      {SERVICE_CATEGORIES.map((cat, gi) => {
        const items = getServicesByCategory(cat.key);
        return (
          <section key={cat.key} className={`py-20 lg:py-24 ${gi % 2 === 1 ? 'bg-bp-slate-50' : 'bg-white'}`} data-testid={`service-cat-${cat.key}`}>
            <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
              <div className="grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4">
                  <div className={`w-16 h-16 rounded-2xl ${HALO[cat.key]} flex items-center justify-center`}>{ICONS[cat.key]}</div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft mt-5">0{gi + 1} / 0{SERVICE_CATEGORIES.length}</div>
                  <h2 className="h-display text-[34px] sm:text-[42px] mt-2">{cat.title}</h2>
                  <p className="mt-4 text-[16px] text-bp-slate leading-relaxed max-w-md">{cat.blurb}</p>
                </div>

                <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                  {items.map((item) => (
                    <Reveal as="article" key={item.slug} className="card-soft p-6 group">
                      <Link to={`/services/${item.slug}`} className="block" data-testid={`service-link-${item.slug}`}>
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-display font-bold text-bp-ink text-[17px] leading-snug group-hover:text-bp-blue transition-colors">{item.name}</h3>
                          <ArrowRight size={16} className="text-bp-slate-soft group-hover:text-bp-blue group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1"/>
                        </div>
                        <p className="mt-2.5 text-[14px] leading-relaxed text-bp-slate">{item.short}</p>
                        <span className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-bp-blue">Learn More <ArrowRight size={13}/></span>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* MOST POPULAR FEATURED - SEO */}
      <section className="py-20 lg:py-24 bg-bp-ink text-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-bp-amber/15 border border-bp-amber/30 rounded-full px-3 py-1">
              <Star size={12} className="text-bp-amber fill-bp-amber"/>
              <span className="text-[12px] font-semibold uppercase tracking-wider text-bp-amber">Most Popular Service</span>
            </div>
            <h2 className="font-display font-extrabold text-[36px] sm:text-[52px] leading-[1.02] mt-5">
              Search Engine <span className="grad-text">Optimization</span>
            </h2>
            <p className="mt-5 text-[17px] text-white/85 leading-relaxed max-w-2xl">
              Our SEO strategies have helped hundreds of businesses dominate Google rankings. Sustainable,
              white-hat techniques that deliver long-term results.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                'Technical SEO audits and optimization',
                'Keyword research and content strategy',
                'Local SEO and Google Business Profile',
                'Link building and authority development',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15.5px] text-white/85"><CheckCircle2 size={20} className="text-bp-teal flex-shrink-0 mt-0.5"/>{b}</li>
              ))}
            </ul>
            <Link to="/services/search-engine-optimization" className="btn-primary mt-8 inline-flex" data-testid="featured-seo-cta">Learn More About SEO <ArrowRight size={15}/></Link>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { v: '+150%', l: 'Avg. Traffic', c: 'text-bp-teal' },
              { v: 'Top 10', l: 'Rankings', c: 'text-bp-amber' },
              { v: '500K+', l: 'Leads', c: 'text-bp-coral' },
              { v: '1k+', l: 'Websites Built', c: 'text-white' },
            ].map((s) => (
              <div key={s.l} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 text-center">
                <div className={`font-display font-extrabold text-[40px] leading-none ${s.c}`}>{s.v}</div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-white/70 mt-3">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="card-floating p-10 lg:p-14 text-center">
            <h2 className="h-display text-[36px] sm:text-[48px]">Ready to Get <span className="grad-text">Started?</span></h2>
            <p className="mt-4 text-[17px] text-bp-slate max-w-xl mx-auto">
              Every business is unique. Request a free estimate and get a custom proposal tailored to your goals and budget.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-primary"><Phone size={15}/> Request A Free Estimate</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
